/**
 * MAVLink FTP 的线格式。
 *
 * 这个文件只回答两个问题：**一条 FTP 请求该是哪 251 个字节**、**收到的 251 个字节是什么**。
 * 会话、重传、队列、进度都在 `client.ts`，两者分开是为了让"协议对不对"能单独测。
 *
 * 依据（2026-09-25 核对）：
 *
 * 1. `MAV_FTP_OPCODE` / `MAV_FTP_ERR` **已经在本仓库的生成表里**（`common.xml` → 生成产物），
 *    所以本文件一个数字都不手抄，全部走 `enumValue()` 查表；查不到就直接抛错，
 *    让"生成的协议表与代码不同步"当场暴露，而不是静默地发出一个别的操作码。
 *    这也顺带解决了上游新增条目（例如 `MAV_FTP_OPCODE_LISTDIRECTORYWITHTIME = 16`）时
 *    不必改这里。
 *
 * 2. payload 的字段顺序与宽度来自 mavlink-devguide `en/services/ftp.md` 的
 *    over-the-wire 表：0-1 `uint16 seq_number`、2 `uint8 session`、3 `uint8 opcode`、
 *    4 `uint8 size`、5 `uint8 req_opcode`、6 `uint8 burst_complete`、7 `uint8 padding`、
 *    8-11 `uint32 offset`、12-250 `uint8 data[239]`（共 251 字节）。
 *    与 ArduPilot `libraries/GCS_MAVLink/GCS_FTP.h` 的 `Transaction` 结构逐字段一致，
 *    也与 QGroundControl `src/Vehicle/FTPManager.cc` 里的
 *    `Q_ASSERT(sizeof(MavlinkFTP::RequestHeader) == 12)` 一致。
 *
 * 3. 应答的 `seq_number`：ArduPilot `setup_reply()` 填的是 `request.seq_number + 1`，
 *    而 mavlink 文档说"重传沿用上一个应答的序号"（即回显）。两种都接受，
 *    见 {@link matchesReply}：在途只有一个请求，放宽不会造成归属歧义。
 */
import {
  decodePayload,
  enumByName,
  enumValue,
  messageById,
  messageIdOf,
  type MavlinkFrame,
} from '@hgc/mavlink';

/** `FILE_TRANSFER_PROTOCOL`，线上长度 254（3 个目标字段 + 251 的 payload）。 */
export const FTP_MESSAGE_NAME = 'FILE_TRANSFER_PROTOCOL';

/** FTP payload 的总长（`data[239]` 与 12 字节头）。 */
export const FTP_PAYLOAD_LENGTH = 251;

/** 载荷区长度，也就是单次请求/应答能带的业务字节上限。 */
export const FTP_DATA_LENGTH = 239;

/** FTP 头的长度（`data` 之前的固定部分）。 */
export const FTP_HEADER_LENGTH = 12;

/** `MAV_FTP_OPCODE_*` 的键。名字与枚举条目一一对应，查表时由 {@link OPCODE_ENTRY} 翻译。 */
export type FtpOpcodeKey =
  | 'none'
  | 'terminateSession'
  | 'resetSessions'
  | 'listDirectory'
  | 'openFileRo'
  | 'readFile'
  | 'createFile'
  | 'writeFile'
  | 'removeFile'
  | 'createDirectory'
  | 'removeDirectory'
  | 'openFileWo'
  | 'truncateFile'
  | 'rename'
  | 'calcFileCrc32'
  | 'burstReadFile'
  | 'listDirectoryWithTime'
  | 'ack'
  | 'nak';

/** `MAV_FTP_ERR_*` 的键。 */
export type FtpErrorKey =
  | 'none'
  | 'fail'
  | 'failErrno'
  | 'invalidDataSize'
  | 'invalidSession'
  | 'noSessionsAvailable'
  | 'eof'
  | 'unknownCommand'
  | 'fileExists'
  | 'fileProtected'
  | 'fileNotFound';

const OPCODE_ENTRY: Readonly<Record<FtpOpcodeKey, string>> = {
  none: 'MAV_FTP_OPCODE_NONE',
  terminateSession: 'MAV_FTP_OPCODE_TERMINATESESSION',
  resetSessions: 'MAV_FTP_OPCODE_RESETSESSION',
  listDirectory: 'MAV_FTP_OPCODE_LISTDIRECTORY',
  openFileRo: 'MAV_FTP_OPCODE_OPENFILERO',
  readFile: 'MAV_FTP_OPCODE_READFILE',
  createFile: 'MAV_FTP_OPCODE_CREATEFILE',
  writeFile: 'MAV_FTP_OPCODE_WRITEFILE',
  removeFile: 'MAV_FTP_OPCODE_REMOVEFILE',
  createDirectory: 'MAV_FTP_OPCODE_CREATEDIRECTORY',
  removeDirectory: 'MAV_FTP_OPCODE_REMOVEDIRECTORY',
  openFileWo: 'MAV_FTP_OPCODE_OPENFILEWO',
  truncateFile: 'MAV_FTP_OPCODE_TRUNCATEFILE',
  rename: 'MAV_FTP_OPCODE_RENAME',
  calcFileCrc32: 'MAV_FTP_OPCODE_CALCFILECRC',
  burstReadFile: 'MAV_FTP_OPCODE_BURSTREADFILE',
  listDirectoryWithTime: 'MAV_FTP_OPCODE_LISTDIRECTORYWITHTIME',
  ack: 'MAV_FTP_OPCODE_ACK',
  nak: 'MAV_FTP_OPCODE_NAK',
};

const ERROR_ENTRY: Readonly<Record<FtpErrorKey, string>> = {
  none: 'MAV_FTP_ERR_NONE',
  fail: 'MAV_FTP_ERR_FAIL',
  failErrno: 'MAV_FTP_ERR_FAILERRNO',
  invalidDataSize: 'MAV_FTP_ERR_INVALIDDATASIZE',
  invalidSession: 'MAV_FTP_ERR_INVALIDSESSION',
  noSessionsAvailable: 'MAV_FTP_ERR_NOSESSIONSAVAILABLE',
  eof: 'MAV_FTP_ERR_EOF',
  unknownCommand: 'MAV_FTP_ERR_UNKNOWNCOMMAND',
  fileExists: 'MAV_FTP_ERR_FILEEXISTS',
  fileProtected: 'MAV_FTP_ERR_FILEPROTECTED',
  fileNotFound: 'MAV_FTP_ERR_FILENOTFOUND',
};

function lookup(enumName: string, entryName: string): number {
  const value = enumValue(enumName, entryName);
  if (value === null) {
    throw new Error(
      `协议表里没有 ${enumName}.${entryName}：生成的枚举表与本代码不同步，请重新运行 npm run gen:mavlink`,
    );
  }
  return value;
}

let opcodeTable: Readonly<Record<FtpOpcodeKey, number>> | null = null;
let errorTable: Readonly<Record<FtpErrorKey, number>> | null = null;

function opcodes(): Readonly<Record<FtpOpcodeKey, number>> {
  if (opcodeTable !== null) return opcodeTable;
  const built = {} as Record<FtpOpcodeKey, number>;
  for (const key of Object.keys(OPCODE_ENTRY) as FtpOpcodeKey[]) {
    built[key] = lookup('MAV_FTP_OPCODE', OPCODE_ENTRY[key]);
  }
  opcodeTable = Object.freeze(built);
  return opcodeTable;
}

function errors(): Readonly<Record<FtpErrorKey, number>> {
  if (errorTable !== null) return errorTable;
  const built = {} as Record<FtpErrorKey, number>;
  for (const key of Object.keys(ERROR_ENTRY) as FtpErrorKey[]) {
    built[key] = lookup('MAV_FTP_ERR', ERROR_ENTRY[key]);
  }
  errorTable = Object.freeze(built);
  return errorTable;
}

/** `MAV_FTP_OPCODE_*` 的数值。查不到时抛错（见文件顶部第 1 条）。 */
export function ftpOpcode(key: FtpOpcodeKey): number {
  return opcodes()[key];
}

/** `MAV_FTP_ERR_*` 的数值。 */
export function ftpErrorCode(key: FtpErrorKey): number {
  return errors()[key];
}

/** 数值 → 枚举名，用于排障显示；不在表里时返回 `null`。 */
export function ftpOpcodeName(value: number): string | null {
  const entries = enumByName('MAV_FTP_OPCODE')?.entries ?? [];
  return entries.find((entry) => entry.value === value)?.name ?? null;
}

/** 数值 → 枚举名，用于排障显示；不在表里时返回 `null`。 */
export function ftpErrorName(value: number): string | null {
  const entries = enumByName('MAV_FTP_ERR')?.entries ?? [];
  return entries.find((entry) => entry.value === value)?.name ?? null;
}

/* ───────────────────────────── 序号 ───────────────────────────── */

/**
 * FTP 的 `seq_number` 是 uint16，长传输一定会回绕。
 *
 * 抽成纯函数是为了能直接测回绕边界：回绕写错的表现是"传了几万帧以后突然每一帧都被
 * 当成不匹配的应答"，而那时用户已经在等一个几十 MB 的文件。
 */
export function nextFtpSequence(sequence: number): number {
  return (sequence + 1) % 0x10000;
}

/** 判断回帧序号是否是本请求的应答（接受"回显"与"+1"两种实现）。 */
export function matchesReplySequence(replySequence: number, requestSequence: number): boolean {
  return (
    replySequence === requestSequence || replySequence === nextFtpSequence(requestSequence)
  );
}

/* ───────────────────────────── 编解码 ───────────────────────────── */

/** 一条 FTP 请求的字段。`size` 与 `data` 的语义随操作码变化，见各字段说明。 */
export interface FtpRequestInit {
  readonly opcode: number;
  readonly session: number;
  readonly sequence: number;
  /**
   * `size` 字段。
   *
   * 它不是"data 的长度"那么简单：
   *   - 路径类操作（OpenFileRO / CreateFile / RemoveFile / …）：路径字节数（含结尾 NUL）；
   *   - ReadFile / BurstReadFile：**要读的字节数**（`data` 不参与）；
   *   - WriteFile / TerminateSession：`data` 里有效字节数；
   *   - ResetSessions：0。
   */
  readonly size: number;
  readonly offset?: number;
  readonly data?: Uint8Array;
}

/** 组一个 251 字节的 FTP payload。 */
export function encodeFtpPayload(init: FtpRequestInit): Uint8Array {
  const payload = new Uint8Array(FTP_PAYLOAD_LENGTH);
  const sequence = init.sequence & 0xffff;
  payload[0] = sequence & 0xff;
  payload[1] = (sequence >> 8) & 0xff;
  payload[2] = init.session & 0xff;
  payload[3] = init.opcode & 0xff;
  payload[4] = init.size & 0xff;
  // 5（req_opcode）、6（burst_complete）、7（padding）在请求里都为 0
  const offset = (init.offset ?? 0) >>> 0;
  payload[8] = offset & 0xff;
  payload[9] = (offset >>> 8) & 0xff;
  payload[10] = (offset >>> 16) & 0xff;
  payload[11] = (offset >>> 24) & 0xff;
  const data = init.data;
  if (data !== undefined) {
    const length = Math.min(data.length, FTP_DATA_LENGTH);
    payload.set(data.subarray(0, length), FTP_HEADER_LENGTH);
  }
  return payload;
}

/** 解码后的一条 FTP 应答（ACK 或 NAK）。 */
export interface FtpReply {
  readonly sequence: number;
  /** 对端这次操作使用的会话号 */
  readonly session: number;
  readonly opcode: number;
  /** 应答的是哪个请求操作码 */
  readonly reqOpcode: number;
  /** `data` 里有效字节数；NAK 时是错误信息长度（1 或 2） */
  readonly size: number;
  readonly burstComplete: boolean;
  readonly offset: number;
  /** 只包含前 `size` 个字节的视图 */
  readonly data: Uint8Array;
  readonly systemId: number;
  readonly componentId: number;
  readonly at: number;
}

let cachedMessageId: number | null | undefined;

/** `FILE_TRANSFER_PROTOCOL` 的消息 ID（110）；协议表里没有时返回 `null`。 */
export function ftpMessageId(): number | null {
  if (cachedMessageId === undefined) cachedMessageId = messageIdOf(FTP_MESSAGE_NAME) ?? null;
  return cachedMessageId;
}

/**
 * 从一帧里解出 FTP 应答；长度不足、解码失败时返回 `null`
 * （调用方只需"忽略这一帧"，不需要区分原因）。
 *
 * **刻意不检查 `target_system` / `target_component`**：这个字段各家填法不一
 * （ArduPilot `send_reply()` 填请求方，也就是地面站自己；有的实现填机体；有的填 0），
 * 拿它做过滤会把有效应答挡在门外 —— 而"这一帧是不是我们那条请求的应答"是由
 * `req_opcode` + `seq_number`（突发流改用 `session` + `req_opcode`）唯一决定的，
 * 那才是可靠判据。
 */
export function decodeFtpReply(frame: MavlinkFrame): FtpReply | null {
  if (frame.messageId !== ftpMessageId()) return null;
  const definition = messageById(frame.messageId);
  if (definition === undefined) return null;

  let payload: Uint8Array;
  try {
    const values = decodePayload(definition, frame.payload);
    const raw = values['payload'];
    if (!(raw instanceof Uint8Array) || raw.length < FTP_PAYLOAD_LENGTH) return null;
    payload = raw;
  } catch {
    // 长度不足的帧：解析器应当已补齐，真出现就当没收到，不当成"对端乱说话"
    return null;
  }

  // 注意：`payload` **就是** FTP 的 251 字节载荷，协议头从下标 0 开始 ——
  // 消息的 target_* 字段不在其中（曾经在这里多加了 2 个字节的偏移，结果是每一帧
  // 应答都被解成垃圾、全部超时）
  const size = Math.min(payload[4] ?? 0, FTP_DATA_LENGTH);
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  return {
    sequence: view.getUint16(0, true),
    session: payload[2] ?? 0,
    opcode: payload[3] ?? 0,
    reqOpcode: payload[5] ?? 0,
    size,
    burstComplete: (payload[6] ?? 0) !== 0,
    offset: view.getUint32(8, true),
    data: payload.subarray(FTP_HEADER_LENGTH, FTP_HEADER_LENGTH + size),
    systemId: frame.systemId,
    componentId: frame.componentId,
    at: frame.receivedAt,
  };
}

const textEncoder = new TextEncoder();

/**
 * 路径 → 字节串（UTF-8 + 结尾 NUL）。
 *
 * 对端（ArduPilot）用 `strnlen()` 判长度，因此**必须**带上 NUL：只给长度不给 NUL 的
 * 写法在 `data[238]` 非零时会被判成 `MAV_FTP_ERR_INVALIDDATASIZE`。
 */
export function encodeFtpPath(path: string): Uint8Array {
  const bytes = textEncoder.encode(path);
  const withNul = new Uint8Array(bytes.length + 1);
  withNul.set(bytes, 0);
  return withNul;
}

/* ───────────────────────── 目录条目 ───────────────────────── */

/** 列表项种类。`skip` 是对端明确说"这一条我没发给你"的占位（名字里有制表符）。 */
export type FtpDirectoryEntryKind = 'file' | 'directory' | 'skip';

export interface FtpRawDirectoryEntry {
  readonly kind: FtpDirectoryEntryKind;
  readonly name: string;
  readonly sizeBytes: number | null;
  readonly modifiedAt: number | null;
}

/** 对端回帧不符合协议时抛这个（与链路错误、NACK 区分开）。 */
export class FtpProtocolError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FtpProtocolError';
  }
}

const textDecoder = new TextDecoder('utf-8', { fatal: false });

/**
 * 解析一页目录列表。数据是若干 NUL 结尾的字符串，格式（ArduPilot `gen_dir_entry()`）：
 *   `F<name>\t<size>[\t<mtime>]\0`、`D<name>[\t0\t<mtime>]\0`、`S\0`。
 *
 * 刻意**不**支持更早的"固定 32 字节二进制条目"格式（只有很旧的 PX4/QGC 服务端会发）：
 * 那套格式的类型字节与字段边界在上游文档里没有权威定义，照猜会解析出垃圾名字并静默地
 * 显示给用户；宁可明确报"格式不认识"。
 */
export function parseDirectoryEntries(data: Uint8Array): readonly FtpRawDirectoryEntry[] {
  const entries: FtpRawDirectoryEntry[] = [];
  let position = 0;
  while (position < data.length) {
    let end = position;
    while (end < data.length && (data[end] ?? 0) !== 0) end += 1;
    if (end === position) {
      // 空串（连续两个 NUL）：整页结束。对端把 data 剩余部分清零后会留下这种尾巴
      if (end === data.length - 1 || (data[end] ?? 0) === 0) break;
    }
    const record = textDecoder.decode(data.subarray(position, end));
    position = end + 1;

    const prefix = record.slice(0, 1);
    const body = record.slice(1);
    const fields = body.split('\t');
    const name = fields[0] ?? '';
    const sizeText = fields[1];
    const timeText = fields[2];
    const sizeBytes =
      sizeText === undefined || sizeText.length === 0 ? null : Number.parseInt(sizeText, 10);
    const modifiedAt =
      timeText === undefined || timeText.length === 0 ? null : Number.parseInt(timeText, 10);

    if (prefix === 'F') {
      entries.push({
        kind: 'file',
        name,
        sizeBytes: Number.isFinite(sizeBytes) ? sizeBytes : null,
        modifiedAt: Number.isFinite(modifiedAt) ? modifiedAt : null,
      });
      continue;
    }
    if (prefix === 'D') {
      entries.push({
        kind: 'directory',
        name,
        // 目录没有有意义的长度，对端固定写 0；这里如实报 null 而不是 0
        sizeBytes: null,
        modifiedAt: Number.isFinite(modifiedAt) ? modifiedAt : null,
      });
      continue;
    }
    if (prefix === 'S') {
      entries.push({ kind: 'skip', name: '', sizeBytes: null, modifiedAt: null });
      continue;
    }
    const byte = prefix.length === 0 ? 0 : (prefix.codePointAt(0) ?? 0);
    throw new FtpProtocolError(
      `目录条目的首字节是 0x${byte.toString(16).padStart(2, '0')}，既不是 F（文件）、D（目录）也不是 S（跳过）：对端可能用了 HGC 不支持的旧版条目格式`,
    );
  }
  return entries;
}

/* ───────────────────────── 错误语义 ───────────────────────── */

/**
 * 收到 NACK 之后该怎么办。
 *
 * 这个区分是**必须**的：所有 NACK 都当成"重试一下也许就好"会让"文件不存在"这种
 * 确定性错误被重发 4 次、白占链路；都当成"就此结束"又会让"会话被对端超时回收"
 * 这种可恢复情况直接失败。依据是 `MAV_FTP_ERR` 各条目的语义（见 mavlink 文档
 * "NAK Error Information"）。
 */
export type FtpRecovery =
  /** 重发同一条请求即可（临时性） */
  | 'retry'
  /** 先 `ResetSessions` 再重试（对端会话槽被占满） */
  | 'reset-sessions'
  /** 本次会话已失效：要重新打开文件并从当前偏移续传 */
  | 'reopen-session'
  /** 确定性失败，重试没有意义 */
  | 'none';

export interface FtpErrorDescription {
  /** `MAV_FTP_ERR` 的枚举名；未知码时为 `null` */
  readonly name: string | null;
  /** 面向飞手的中文原因 */
  readonly text: string;
  readonly recovery: FtpRecovery;
}

/**
 * errno → 中文。
 *
 * 数值取 newlib/POSIX 的定义（ArduPilot 在 ChibiOS 上用的就是 newlib 的 errno，
 * 而它已经替我们把 `EEXIST`/`ENOENT` 翻译成了 `FILEEXISTS`/`FILENOTFOUND`，
 * 所以留到这里的其它值都是原样透传的 errno）。FATFS 这类文件系统可能给的是自己的
 * 编号，因此**认不出就如实报编号**，不猜。
 */
const ERRNO_TEXT: Readonly<Record<number, string>> = {
  1: '没有权限（EPERM）',
  2: '文件或目录不存在（ENOENT）',
  5: '对端存储读写错误（EIO）',
  9: '文件句柄无效（EBADF）：对端可能已经把这个会话的文件关掉了',
  11: '对端正忙，稍后可用（EAGAIN）',
  12: '对端内存不足（ENOMEM）',
  13: '权限不足（EACCES）',
  16: '存储被占用（EBUSY）：稍后重试',
  17: '同名文件或目录已经存在（EEXIST）',
  18: '不能跨文件系统移动（EXDEV）',
  20: '路径中间有一段不是目录（ENOTDIR）',
  21: '这是一个目录，不能按文件打开（EISDIR）',
  22: '参数不合法（EINVAL）',
  27: '文件太大（EFBIG）',
  28: '对端存储空间不足（ENOSPC）',
  30: '对端文件系统只读（EROFS）',
  36: '路径太长（ENAMETOOLONG）',
  39: '目录非空（ENOTEMPTY）',
};

/** errno 语义里属于"再试一次可能就好了"的少数几个。 */
const TRANSIENT_ERRNO: ReadonlySet<number> = new Set([4 /* EINTR */, 11 /* EAGAIN */, 16 /* EBUSY */]);

export function describeErrno(errno: number): string {
  return ERRNO_TEXT[errno] ?? `对端文件系统错误（errno ${errno}，含义取决于它对端的文件系统）`;
}

/**
 * 把 NACK 的 `data[0]`（+ 可选 `data[1]` errno）翻成"为什么 + 还能怎么办"。
 *
 * `context` 用来让文案具体化：同样是 `MAV_FTP_ERR_EOF`，在读文件时是"读完了"，
 * 在列目录时是"目录条目读完了"，而在写文件时是"对端认为偏移越界了"。
 */
export function describeFtpError(
  code: number,
  errno: number | null,
  context: { readonly opcode: number; readonly path: string | null },
): FtpErrorDescription {
  const name = ftpErrorName(code);
  const where = context.path === null ? '这次操作' : `${context.path}`;
  const key = errorKeyOf(code);

  switch (key) {
    case 'none':
      return {
        name,
        text: `对端用错误码 0（MAV_FTP_ERR_NONE）回了 NAK —— 这是对端实现的问题，${where}没有成功`,
        recovery: 'none',
      };
    case 'fail':
      return {
        name,
        text:
          `对端拒绝了这次操作（MAV_FTP_ERR_FAIL，未给出具体原因）。对端同一会话里只能打开一个文件，` +
          `若上一次传输没有正常结束，先 ResetSessions 再试`,
        recovery: 'reopen-session',
      };
    case 'failErrno': {
      if (errno === null) {
        return {
          name,
          text: '对端报告了一个文件系统错误（MAV_FTP_ERR_FAILERRNO），但没有给出 errno',
          recovery: 'none',
        };
      }
      return {
        name,
        text: describeErrno(errno),
        recovery: TRANSIENT_ERRNO.has(errno) ? 'retry' : 'none',
      };
    }
    case 'invalidDataSize':
      return {
        name,
        text:
          '对端认为请求长度不合法（MAV_FTP_ERR_INVALIDDATASIZE）：路径必须以 NUL 结尾且不超过 239 字节，' +
          '读/写请求的字节数也必须落在 1–239',
        recovery: 'none',
      };
    case 'invalidSession':
      return {
        name,
        text:
          '会话已失效（MAV_FTP_ERR_INVALIDSESSION）：对端把这次会话关掉了（ArduPilot 在会话空闲 3 秒、' +
          '或被回收 20 秒后关闭），需要重新打开文件再续传',
        recovery: 'reopen-session',
      };
    case 'noSessionsAvailable':
      return {
        name,
        text:
          '对端的 FTP 会话槽已用尽（MAV_FTP_ERR_NOSESSIONSAVAILABLE）：' +
          '多半是上一次连接（或另一台地面站）留下的会话还没过期，先 ResetSessions 再试',
        recovery: 'reset-sessions',
      };
    case 'eof':
      return {
        name,
        text: '已经读到文件/目录的末尾（MAV_FTP_ERR_EOF）',
        recovery: 'none',
      };
    case 'unknownCommand':
      return {
        name,
        text: '对端不支持这个操作（MAV_FTP_ERR_UNKNOWNCOMMAND）：固件版本可能太旧',
        recovery: 'none',
      };
    case 'fileExists':
      return {
        name,
        text: '目标已经存在（MAV_FTP_ERR_FILEEXISTS）。要覆盖它请用 CreateFile（会截断），而不是 OpenFileWO',
        recovery: 'none',
      };
    case 'fileProtected':
      return {
        name,
        text: '文件或目录受写保护（MAV_FTP_ERR_FILEPROTECTED）：SD 卡写保护、只读文件系统或该文件被固件占用',
        recovery: 'none',
      };
    case 'fileNotFound':
      return {
        name,
        text:
          `${where} 不存在（MAV_FTP_ERR_FILENOTFOUND）。请确认路径是绝对路径（ArduPilot 的虚拟目录以 @ 开头，` +
          `例如 @SYS/、@PARAM/、@MAV_LOG/）且大小写正确`,
        recovery: 'none',
      };
    default:
      return {
        name,
        text: `对端返回了未知的 FTP 错误码 ${code}`,
        recovery: 'none',
      };
  }
}

/**
 * 数值 → 键。
 *
 * 反向查一次表而不是 `switch (code)` 硬编码数字：上游给这些枚举重排过值（例如
 * `MAV_FTP_ERR_EOF` 曾经是 5），硬编码会在升级协议表后静默地把语义搞错。
 */
function errorKeyOf(code: number): FtpErrorKey | null {
  for (const key of Object.keys(ERROR_ENTRY) as FtpErrorKey[]) {
    if (errors()[key] === code) return key;
  }
  return null;
}
